import { Suspense, lazy } from 'react'
import { Navigate } from 'react-router-dom'
import { RouteObject } from 'react-router'
import { Helmet } from 'react-helmet-async'

import SidebarLayout from './layouts/SidebarLayout'
import BaseLayout from './layouts/BaseLayout'

import SuspenseLoader from './shared/components/SuspenseLoader'
import {
  loginPath,
  forgotPasswordPath,
  acceptInvitePath,
  resetPasswordPath,
  subscribersPath,
  subscriberDetailsPath,
  subscriptionPath,
  subscriptionMetricsPath,
  aiSpendPath,
  assistantConfigPath,
  settingsPath,
  settingsSessionsPath,
  adminPath,
  supportPath,
  impersonationAuditPath,
} from './logic/paths'
import AuthRedirect, { AuthRedirect1, RequireSuperAdmin } from './shared/utilities/Auth/AuthRedirect'
import SubscriberDetailComponent from './modules/subscribers/subscriberDetail'
import Admin from './modules/admin'
import Support from './modules/support'

const APP_NAME = 'PieceWorkPro Admin Panel'

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader left="10%" />}>
      <Component {...props} />
    </Suspense>
  )

const withTitle = (title: string, element: JSX.Element) => (
  <>
    <Helmet>
      <title>{title ? `${title} | ${APP_NAME}` : APP_NAME}</title>
    </Helmet>
    {element}
  </>
)

// Pages

const LoginAdmin = Loader(lazy(() => import('./modules/LoginAdmin')))
// Public account-recovery / invite-acceptance pages.
//
// `/forgot-password` stays in the AuthRedirect branch below: an admin who
// already holds a session has no business requesting a reset link, and the
// bounce to /subscribers is the right answer for them.
//
// `/accept-invite/:token` and `/reset-password/:token` do NOT — see the
// dedicated top-level branch further down.
const ForgotPassword = Loader(lazy(() => import('./modules/auth/ForgotPassword')))
const AcceptInvite = Loader(lazy(() => import('./modules/auth/AcceptInvite')))
const ResetPassword = Loader(lazy(() => import('./modules/auth/ResetPassword')))
const Subscribers = Loader(lazy(() => import('./modules/subscribers')))
const Subscription = Loader(lazy(() => import('./modules/subscription')))
const SubscriptionMetrics = Loader(lazy(() => import('./modules/subscriptionMetrics')))
const AiSpend = Loader(lazy(() => import('./modules/aiUsage')))
const AssistantConfig = Loader(lazy(() => import('./modules/assistantConfig')))
const Settings = Loader(lazy(() => import('./modules/settings')))
const AdminSessions = Loader(lazy(() => import('./modules/settings/sessions')))
const ImpersonationAudit = Loader(lazy(() => import('./modules/impersonationAudit')))

// Status

const Status404 = Loader(lazy(() => import('./modules/pages/Status/Status404')))

const routes: RouteObject[] = [
  {
    path: '',
    element: (
      <AuthRedirect>
        <BaseLayout />
      </AuthRedirect>
    ),
    children: [
      {
        path: '',
        element: <Navigate to={loginPath} replace />,
      },
      {
        path: loginPath,
        element: withTitle('', <LoginAdmin />),
      },
      {
        path: forgotPasswordPath,
        element: withTitle('Forgot Password', <ForgotPassword />),
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />,
          },
          {
            path: '404',
            element: withTitle('Page Not Found', <Status404 />),
          },
        ],
      },
      {
        path: '*',
        element: withTitle('Page Not Found', <Status404 />),
      },
    ],
  },
  // Token pages — SESSION-AGNOSTIC by design, so deliberately outside
  // `AuthRedirect` (which bounces anyone holding a token to /subscribers).
  //
  // The retirement sequence these exist for is: invite yourself as Super Admin
  // FROM the shared login, then open the emailed link. The browser is still
  // signed in as the shared account at that moment, so inside AuthRedirect the
  // invite page silently bounced and the flow was unreachable. The same applies
  // to a reset link opened from a stale session — both pages are about to
  // REPLACE the session that is holding them out. Same BaseLayout, no guard.
  {
    path: '',
    element: <BaseLayout />,
    children: [
      {
        path: acceptInvitePath,
        element: withTitle('Set Your Password', <AcceptInvite />),
      },
      {
        path: resetPasswordPath,
        element: withTitle('Choose a New Password', <ResetPassword />),
      },
    ],
  },
  {
    path: '',
    element: (
      <AuthRedirect1>
        <SidebarLayout />
      </AuthRedirect1>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="subscribers" replace />,
      },
      {
        path: subscribersPath,
        element: withTitle('Subscribers', <Subscribers />),
      },
      {
        path: subscriberDetailsPath,
        element: withTitle('Subscriber Details', <SubscriberDetailComponent />),
      },
      {
        path: subscriptionPath,
        element: withTitle('Subscription', <Subscription />),
      },
      {
        path: subscriptionMetricsPath,
        element: withTitle('Subscription Metrics', <SubscriptionMetrics />),
      },
      {
        path: aiSpendPath,
        element: withTitle('AI Spend', <AiSpend />),
      },
      // Kill switch + rollout + caps: SuperAdmin-only server-side, same guard
      // and redirect-with-toast as /admin. The page also renders its own 403
      // state for a stored role that has gone stale mid-session.
      {
        path: assistantConfigPath,
        element: withTitle(
          'PWP Assistant',
          <RequireSuperAdmin>
            <AssistantConfig />
          </RequireSuperAdmin>
        ),
      },
      {
        path: settingsPath,
        element: withTitle('Settings', <Settings />),
      },
      {
        path: settingsSessionsPath,
        element: withTitle('Active Sessions', <AdminSessions />),
      },
      // Admin accounts CRUD is SuperAdmin-only end to end (add / update role /
      // disable / delete / resend invite all check the role server-side), and
      // the sidebar hides the entry — but hiding a nav item is not a guard, so
      // a typed or bookmarked /admin used to render the page with every request
      // 400ing. RequireSuperAdmin bounces a plain Admin to Subscribers.
      {
        path: adminPath,
        element: withTitle(
          'Admin',
          <RequireSuperAdmin>
            <Admin />
          </RequireSuperAdmin>
        ),
      },
      {
        path: supportPath,
        element: withTitle('Support', <Support />),
      },
      {
        path: impersonationAuditPath,
        element: withTitle('Impersonation audit', <ImpersonationAudit />),
      },
      {
        path: '*',
        element: withTitle('Page Not Found', <Status404 />),
      },
    ],
  },
]

export default routes
